// Rootegra LLC ledger types — mirrors migration 278 (docs/backbar-accounting/CONTRACT-2026-10-16.md).
// Every amount is integer cents.

export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'cogs' | 'expense';
export type NormalBalance = 'debit' | 'credit';
export type Member = 'leah' | 'laura';
export type AccessLevel = 'view' | 'edit';
export type EntrySource =
  | 'manual' | 'opening' | 'apple_month' | 'apple_payment' | 'shared_cost' | 'adjustment'
  | 'distribution' | 'contribution' | 'depreciation' | 'disposal' | 'bank' | 'income' | 'expense' | 'reversal';
export type TaxLine =
  | 'gross_receipts' | 'returns' | 'cogs' | 'other_income' | 'guaranteed_payments' | 'repairs'
  | 'bad_debts' | 'rent' | 'taxes_licenses' | 'interest' | 'depreciation' | 'other_deductions';

export type Account = {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  subtype: string;
  normal_balance: NormalBalance;
  member: Member | null;
  tax_line: TaxLine | null;
  display_order: number;
  is_active: boolean;
  is_system: boolean;
};

export type JournalLine = {
  id: string;
  entry_id: string;
  line_no: number;
  account_id: string;
  debit_cents: number;
  credit_cents: number;
  memo: string | null;
  member: Member | null;
  code?: string;
  name?: string;
};

export type JournalEntry = {
  id: string;
  entry_date: string;
  memo: string | null;
  source: EntrySource;
  source_ref: string | null;
  details: Record<string, unknown>;
  reverses_id: string | null;
  is_reversed: boolean;
  posted_by: string | null;
  created_at: string;
  lines: JournalLine[];
};

/** What the UI sends to post or update an entry. account_id or account_code, one of them. */
export type LineInput = {
  account_id?: string;
  account_code?: string;
  debit_cents: number;
  credit_cents: number;
  memo?: string | null;
  member?: Member | null;
};

export type Period = {
  month: string;
  status: 'open' | 'closed';
  closed_at: string | null;
  closed_by: string | null;
  reopened_at: string | null;
  reopened_by: string | null;
  summary: Record<string, unknown> | null;
};

export type Vendor = {
  id: string;
  slug: string;
  name: string;
  default_account_id: string | null;
  share_pct: number | null;
  match_text: string[];
  notes: string | null;
  is_active: boolean;
  created_at: string;
};

export type BankAccount = {
  id: string;
  name: string;
  kind: 'bank' | 'vessel' | 'credit_card' | 'other';
  account_id: string;
  institution: string | null;
  last4: string | null;
  is_active: boolean;
  created_at: string;
};

export type BankTransaction = {
  id: string;
  bank_account_id: string;
  posted_date: string;
  amount_cents: number;
  description: string | null;
  counterparty: string | null;
  external_id: string | null;
  raw: Record<string, unknown> | null;
  status: 'for_review' | 'posted' | 'excluded';
  entry_id: string | null;
  account_id: string | null;
  vendor_id: string | null;
  note: string | null;
  created_at: string;
};

export type BankImportRow = {
  posted_date: string;
  amount_cents: number;
  description: string;
  counterparty?: string | null;
  external_id?: string | null;
  raw?: Record<string, unknown> | null;
};

export type BankImportResult = { import_id: string; rows_in: number; rows_new: number; rows_dup: number };

export type BankReconciliation = {
  id: string;
  bank_account_id: string;
  statement_date: string;
  statement_balance_cents: number;
  ledger_balance_cents: number;
  difference_cents: number;
  note: string | null;
  done_by: string | null;
  created_at: string;
};

export type FixedAsset = {
  id: string;
  name: string;
  description: string | null;
  category: 'equipment' | 'furniture' | 'software' | 'other';
  acquired_on: string;
  in_service_on: string;
  cost_cents: number;
  tax_basis_cents: number | null;
  salvage_cents: number;
  life_months: number;
  method: 'straight_line';
  asset_account_id: string;
  accum_depr_account_id: string;
  depr_expense_account_id: string;
  source: 'purchase' | 'contribution' | 'opening';
  contributed_by: Member | null;
  consent_ref: string | null;
  entry_id: string | null;
  disposed_on: string | null;
  disposal_note: string | null;
  disposal_entry_id: string | null;
  created_at: string;
};

export type AssetInput = {
  name: string;
  description?: string | null;
  category: FixedAsset['category'];
  acquired_on: string;
  in_service_on: string;
  cost_cents: number;
  tax_basis_cents?: number | null;
  salvage_cents?: number;
  life_months: number;
  source: FixedAsset['source'];
  paid_from_account_id?: string | null;
  contributed_by?: Member | null;
  consent_ref?: string | null;
};

export type DepreciationRun = { id: string; asset_id: string; month: string; amount_cents: number; entry_id: string; created_at: string };

export type AccountMap = {
  vessel: string;
  due_from_apple: string;
  apple_sales: string;
  apple_refunds: string;
  apple_commission: string;
  adjustments: string;
  equipment: string;
  accum_depr: string;
  depr_expense: string;
  opening_equity: string;
  default_shared_cost: string;
  disposal: string;
  capital: Record<Member, string>;
  distributions: Record<Member, string>;
};
export type EntitySettings = { name: string; tax_form: '1065'; fiscal_year_start: number; state: string | null; ein_last4: string | null };
export type MemberSettings = Record<Member, { name: string }>;

export type TrialBalanceRow = {
  account_id: string;
  code: string;
  name: string;
  type: AccountType;
  subtype: string;
  normal_balance: NormalBalance;
  member: Member | null;
  tax_line: TaxLine | null;
  display_order: number;
  is_active: boolean;
  opening_debit_cents: number;
  opening_credit_cents: number;
  debit_cents: number;
  credit_cents: number;
};

export type LedgerLine = {
  entry_id: string;
  entry_date: string;
  memo: string | null;
  source: EntrySource;
  source_ref: string | null;
  is_reversed: boolean;
  line_id: string;
  line_no: number;
  account_id: string;
  code: string;
  name: string;
  debit_cents: number;
  credit_cents: number;
  line_memo: string | null;
  member: Member | null;
};

export type JournalPage = { total: number; entries: JournalEntry[] };

export type MemberCapital = {
  year: number;
  net_income_cents: number;
  members: Record<Member, {
    pct: number;
    beginning_cents: number;
    contributions_cents: number;
    distributions_cents: number;
    allocated_income_cents: number;
    ending_cents: number;
  }>;
};

export type Overview = {
  vessel_cents: number;
  cash_cents: number;
  due_from_apple_cents: number;
  net_income_ytd_cents: number;
  net_income_month_cents: number;
  for_review: number;
  open_months: string[];
  closed_through: string | null;
  last_entry_at: string | null;
  assets_in_service: number;
  members: Record<Member, { capital_cents: number }>;
  apple: { month: string | null; closed: boolean; paid: boolean };
};

export type MyAccess = { can_view: boolean; can_edit: boolean; is_admin: boolean; level: AccessLevel | null };

export type AccessRow = {
  user_id: string;
  email: string | null;
  level: AccessLevel | null;
  is_admin: boolean;
  admin_role: string | null;
  granted_by_email: string | null;
  granted_at: string | null;
  last_sign_in_at: string | null;
};

export type AuditRow = {
  id: string;
  actor: string | null;
  actor_email: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  details: Record<string, unknown>;
  at: string;
};

/** 2025 Form 1065 line labels for tax_line values. Check against the current year's form. */
export const TAX_LINE_LABELS: Record<TaxLine, string> = {
  gross_receipts: '1a Gross receipts or sales',
  returns: '1b Returns and allowances',
  cogs: '2 Cost of goods sold',
  other_income: '7 Other income (loss)',
  guaranteed_payments: '10 Guaranteed payments to partners',
  repairs: '11 Repairs and maintenance',
  bad_debts: '12 Bad debts',
  rent: '13 Rent',
  taxes_licenses: '14 Taxes and licenses',
  interest: '15 Interest',
  depreciation: '16a Depreciation',
  other_deductions: '21 Other deductions',
};

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  income: 'Income',
  cogs: 'Cost of goods sold',
  expense: 'Expenses',
};

export const SOURCE_LABELS: Record<EntrySource, string> = {
  manual: 'Manual entry',
  opening: 'Opening balances',
  apple_month: 'Apple month',
  apple_payment: 'Apple payment',
  shared_cost: 'Shared cost',
  adjustment: 'Adjustment',
  distribution: 'Member distribution',
  contribution: 'Member contribution',
  depreciation: 'Depreciation',
  disposal: 'Asset disposal',
  bank: 'Bank transaction',
  income: 'Income',
  expense: 'Expense',
  reversal: 'Reversal',
};

export const MEMBERS: Member[] = ['leah', 'laura'];
